const ITEM_TONES = { PENDING: "default", FULFILLED: "success", FLAGGED: "error" };

export const itemStateTone = (state) => ITEM_TONES[state] || "default";

const itemFiles = (item) => item?.files || [];

const itemIsReceived = (item) => item?.state === "FULFILLED" || itemFiles(item).length > 0;

/**
 * Deleted by the office with no reupload asked and nothing live left: nothing is owed for the
 * item, so it reads resolved once the round is closed rather than keeping it permanently
 * short. Open rounds keep it owed — the partner may still need to act.
 */
const itemIsRemovedResolved = (item, requestOpen) =>
  !requestOpen &&
  (item?.state === "PENDING" || !item?.state) &&
  itemFiles(item).length === 0 &&
  Boolean(item?.hasRemovedFiles) &&
  !item?.flagReason &&
  !item?.sourceItemId;

export const requestProgress = (request) => {
  const open = request?.status === "ACTIVE";
  const items = request?.items || [];
  const required = items.filter((i) => i.required);
  // Draft files count: partner uploads stay pending until send-to-office, so an item holding
  // files reads fulfilled even while its state still says pending.
  const fulfilled = required.filter(itemIsReceived).length;
  const removed = required.filter(
    (i) => !itemIsReceived(i) && itemIsRemovedResolved(i, open)
  ).length;
  const resolved = Math.min(required.length, fulfilled + removed);
  const percent = required.length ? Math.round((resolved / required.length) * 100) : 0;
  return {
    fulfilled,
    removed,
    resolved,
    required: required.length,
    percent,
    receivedPercent: required.length ? (fulfilled / required.length) * 100 : 0,
    removedPercent: required.length ? (removed / required.length) * 100 : 0,
  };
};

/**
 * Progress for a list row. Rows carry counts and no items — only the detail payload has items —
 * so they cannot go through requestProgress. Office-removed counts only resolve a closed round;
 * on an open round the partner may still owe those files.
 */
export const rowProgress = (row) => {
  const required = Number(row?.requiredTotal) || 0;
  const fulfilled = Number(row?.fulfilledRequired) || 0;
  const removed = row?.status === "ACTIVE" ? 0 : Number(row?.removedRequired) || 0;
  const resolved = Math.min(required, fulfilled + removed);
  const percent = required ? Math.round((resolved / required) * 100) : 0;
  return {
    fulfilled,
    removed,
    resolved,
    required,
    percent,
    receivedPercent: required ? (fulfilled / required) * 100 : 0,
    removedPercent: required ? (removed / required) * 100 : 0,
  };
};

/**
 * The one-line progress copy. Closed rounds with office-removed files read complete ("3 of 3
 * resolved · 2 removed by office") instead of permanently short; everything else keeps the
 * received wording.
 */
export const progressSummary = (progress, requestOpen = true) => {
  const required = Number(progress?.required) || 0;
  const removed = Number(progress?.removed) || 0;
  if (!requestOpen && removed > 0) {
    return `${progress.resolved} of ${required} resolved · ${removed} removed by office`;
  }
  return `${progress?.fulfilled ?? 0} of ${required} required received`;
};

/** Required items first — those are the ones blocking the confirm button. */
export const outstandingItems = (request) =>
  (request?.items || [])
    .filter((i) => i.state !== "FULFILLED")
    .sort((a, b) => Number(b.required) - Number(a.required));

export const canConfirmRequest = (request) => {
  if (request?.status !== "ACTIVE") return false;
  const { fulfilled, required } = requestProgress(request);
  return required > 0 && fulfilled === required;
};

const UPLOADER_LABELS = {
  YOU: "Uploaded by you",
  CLIENT: "Uploaded by the client",
  OFFICE: "Uploaded by the office",
};

export const uploaderLabel = (file) => UPLOADER_LABELS[file?.uploadedBy] || "";

const ITEM_LABELS = { PENDING: "Outstanding", FULFILLED: "Received", FLAGGED: "Needs attention" };

/**
 * Portal label for one item. Accepts either a state string ("PENDING") or the whole item —
 * the item form is what lets a reupload round say "Reupload requested" with its reason and an
 * office deletion say "Removed by office" instead of a bare "Outstanding".
 *
 * `requestOpen` is whether the round can still be acted on (status ACTIVE). Once a reupload
 * round is submitted there is nothing left to ask for, so its items read as resolved rather
 * than repeating the ask: items with files are Received, an optional item left unsent reads
 * Not re-sent.
 */
export const itemStateLabel = (stateOrItem, requestOpen = true) => {
  const item = stateOrItem && typeof stateOrItem === "object" ? stateOrItem : null;
  const state = item ? item.state : stateOrItem;
  if (state === "FLAGGED") return "Needs attention";
  // A submitted reupload round retires what it replaces: the item stays fulfilled but its file
  // is the new copy, so it reads Replaced rather than Received with nothing under it.
  if (state === "FULFILLED") return item?.hasReplacedFiles ? "Replaced" : "Received";
  if (item) {
    if (item.hasRemovedFiles && (item.files || []).length === 0) return "Removed by office";
    if (item.sourceItemId || item.flagReason)
      return requestOpen ? "Reupload requested" : "Not re-sent";
  }
  return ITEM_LABELS[state] || state || "Outstanding";
};

export const itemStateToneOverride = (stateOrItem, requestOpen = true) => {
  const item = stateOrItem && typeof stateOrItem === "object" ? stateOrItem : null;
  const state = item ? item.state : stateOrItem;
  if (state === "FLAGGED") return "error";
  if (state === "FULFILLED") return item?.hasReplacedFiles ? "neutral" : "success";
  if (item && item.hasRemovedFiles && (item.files || []).length === 0) return "error";
  if (item && (item.sourceItemId || item.flagReason)) return requestOpen ? "error" : "default";
  return "default";
};

/** True when this request is a reupload round (a child of another request). */
export const isReuploadRequest = (request) =>
  request?.parentRequestId !== null && request?.parentRequestId !== undefined;

/** True when the office deleted the collected file(s) and nothing live remains. */
export const isRemovedByOffice = (item) =>
  Boolean(item) &&
  item.state !== "FULFILLED" &&
  (item.files || []).length === 0 &&
  Boolean(item.hasRemovedFiles);

/** Short "who put this file here" tag for a file row. */
const SOURCE_LABELS = { YOU: "From you", CLIENT: "From client", OFFICE: "From office" };

export const fileSourceLabel = (file) => SOURCE_LABELS[file?.uploadedBy] || "";

/**
 * Where a request stands, for its status chip. Works on a detail payload (items) and on a list
 * row (counts), since the client page lists rows but opens detail in place.
 */
export const requestStage = (request) => {
  if (request?.status === "REVOKED")
    return { id: "cancelled", label: "Cancelled", tone: "neutral" };
  if (request?.status === "SUBMITTED") {
    return { id: "sent", label: "Sent to office", tone: "emerald" };
  }
  const progress = request?.items ? requestProgress(request) : rowProgress(request);
  if (progress.required > 0 && progress.fulfilled === progress.required) {
    return { id: "ready", label: "Ready to send", tone: "amber" };
  }
  return { id: "collecting", label: "Collecting", tone: "sky" };
};

const STAGE_ORDER = { ready: 0, collecting: 1, sent: 2, cancelled: 3 };

/** The ones waiting on the partner first, then what is done, newest first within each. */
export const sortRequestsForClient = (requests = []) =>
  [...requests].sort(
    (a, b) =>
      STAGE_ORDER[requestStage(a).id] - STAGE_ORDER[requestStage(b).id] ||
      String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
  );
